import { useCallback, useEffect, useMemo, useState } from 'react';
import { Network, getChainConstant, matchVault } from 'nftx.js';
import { useNetwork } from '@nftx/react';
import useVaults from '@/application/read/vaults/useVaults';
import type { SwapState } from '@/domain/swap';
import type { Tab } from '../types';
import { getInitialTab } from './hooks';
import useAddress from '@/application/read/web3/useAddress';
import BuySearchContainer from './BuySearchContainer';
import SellSearchContainer from './SellSearchContainer';
import useSwapState from '@/application/read/swap/useSwapState';
import { useAccount, useBalance } from 'wagmi';

const POPULAR_VAULTS = {
  [Network.Mainnet]: [
    '0', // Punk
    '392', // Milady
    '27', // Chrome Squiggle
    '112', // Pudgy
  ],
};

type SwapDispatch = ReturnType<typeof useSwapState>[1];

export type SwapSearchContainerProps = SwapState & {
  source: 'wallet' | 'vault';
  dispatch: SwapDispatch;
};

const PAGE_SIZE = 12;

export default function SwapSearchContainer({
  source,
  dispatch,
  ...state
}: SwapSearchContainerProps) {
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const network = useNetwork();
  const { data: allVaults } = useVaults();
  const { address } = useAccount();
  const { data: balance } = useBalance({ address });

  const searchedVaultIds = useMemo(() => {
    if (!search || !allVaults) {
      return [];
    }
    const vaultIds: string[] = [];

    allVaults.forEach((v) => {
      if (matchVault(v, search)) {
        vaultIds.push(v.vaultId);
      }
    });

    return vaultIds;
  }, [allVaults, search]);

  const popularVaultIds = useMemo(() => {
    return getChainConstant(POPULAR_VAULTS, network, null);
  }, [network]);

  const [tab, setTab] = useState<Tab>(() =>
    getInitialTab(source === 'wallet' ? state.sellType : state.buyType)
  );

  const userAddress = useAddress();

  const handleShowMore = useCallback(() => {
    setLimit((x) => x + PAGE_SIZE);
  }, []);

  const handleTabChange = useCallback((t: Tab) => {
    setLimit(PAGE_SIZE);
    setTab(t);
  }, []);

  // Reset the page size when searching
  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [search]);

  if (source === 'wallet') {
    return (
      <SellSearchContainer
        {...state}
        ethBalance={balance?.value}
        userAddress={userAddress}
        tab={tab}
        onTabChange={handleTabChange}
        search={search}
        onSearch={setSearch}
        limit={limit}
        onShowMore={handleShowMore}
        searchedVaultIds={searchedVaultIds}
        popularVaultIds={popularVaultIds}
        dispatch={dispatch}
      />
    );
  }

  return (
    <BuySearchContainer
      {...state}
      ethBalance={balance?.value}
      userAddress={userAddress}
      tab={tab}
      onTabChange={handleTabChange}
      search={search}
      onSearch={setSearch}
      limit={limit}
      onShowMore={handleShowMore}
      searchedVaultIds={searchedVaultIds}
      popularVaultIds={popularVaultIds}
      dispatch={dispatch}
    />
  );
}
